import { Currency } from "@generated"
import { useAccount } from "@lib/hooks/use-account"
import { useEffect, useMemo } from "react"
import { create } from "zustand"

const CURRENCY_STORAGE_KEY = "viesus.currency"
const DEFAULT_CURRENCY: Currency = Currency.Usd

const isValidCurrency = (value: unknown): value is Currency => {
  return value === Currency.Usd || value === Currency.Eur
}

type CurrencyStore = {
  currency: Currency
  setCurrency: (next: Currency) => void
}

const useCurrencyStore = create<CurrencyStore>((set) => ({
  currency: DEFAULT_CURRENCY,
  setCurrency: (next) => {
    set({ currency: next })
    try {
      if (typeof window !== "undefined") {
        window.localStorage.setItem(CURRENCY_STORAGE_KEY, next)
      }
    } catch {
      // ignore
    }
  },
}))

export const useCurrency = () => {
  const account = useAccount()
  const stripeSubscriptionCurrency = useMemo(() => {
    return account?.stripeSubscription?.currency?.toUpperCase()
  }, [account])

  const { currency, setCurrency } = useCurrencyStore()

  useEffect(() => {
    if (typeof window === "undefined") return

    // 1. Prefer Stripe subscription currency when valid
    if (
      stripeSubscriptionCurrency &&
      isValidCurrency(stripeSubscriptionCurrency)
    ) {
      setCurrency(stripeSubscriptionCurrency)
      return
    }

    // 2. Fallback to stored currency if valid
    try {
      const stored = window.localStorage.getItem(CURRENCY_STORAGE_KEY)
      if (stored && isValidCurrency(stored)) {
        setCurrency(stored)
        return
      }

      // 3. Ensure default is stored when nothing/invalid
      window.localStorage.setItem(CURRENCY_STORAGE_KEY, DEFAULT_CURRENCY)
      setCurrency(DEFAULT_CURRENCY)
    } catch {
      // ignore access errors (e.g. disabled storage)
    }
  }, [setCurrency, stripeSubscriptionCurrency])

  return { currency, setCurrency }
}
